import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, KpiCard } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LOGO, STATUS_STYLES, fmtDate } from "@/lib/helpers";
import { Plus, Trash2, CheckCircle2, ClipboardList, Percent, Timer, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const empty = { date: today(), time: "09:00", client_name: "", project: "", task_assigned: "", target: 0, completed: 0, accuracy: 0, status: "In Progress", hours_worked: 0, remarks: "" };

export default function Workspace() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = ["founder", "admin"].includes(user?.role);
  const [selectedEmp, setSelectedEmp] = useState(isAdmin ? "" : user?.employee_id);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data, enabled: isAdmin });
  const effectiveEmp = isAdmin ? selectedEmp : user?.employee_id;

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["worklogs", effectiveEmp],
    queryFn: async () => (await api.get(`/worklogs${isAdmin && effectiveEmp ? `?employee_id=${effectiveEmp}` : ""}`)).data,
    enabled: !!effectiveEmp || !isAdmin,
  });

  const { data: summary } = useQuery({
    queryKey: ["worklog-summary", effectiveEmp],
    queryFn: async () => (await api.get(`/worklogs/summary/${effectiveEmp}?date_str=${today()}`)).data,
    enabled: !!effectiveEmp,
  });

  const create = useMutation({
    mutationFn: () => api.post(`/worklogs${isAdmin && effectiveEmp ? `?employee_id=${effectiveEmp}` : ""}`, {
      ...form, target: Number(form.target), completed: Number(form.completed), accuracy: Number(form.accuracy), hours_worked: Number(form.hours_worked),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["worklogs"] }); qc.invalidateQueries({ queryKey: ["worklog-summary"] }); toast.success("Work log added"); setOpen(false); setForm(empty); },
    onError: () => toast.error("Save failed. Select an employee first."),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/worklogs/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["worklogs"] }); qc.invalidateQueries({ queryKey: ["worklog-summary"] }); toast.success("Deleted"); },
  });

  const empInfo = useMemo(() => employees.find((e) => e.employee_id === effectiveEmp), [employees, effectiveEmp]);
  const headerName = isAdmin ? (empInfo?.name || "Select an employee") : user?.name;

  return (
    <div data-testid="workspace-page">
      <PageHeader title="Employee Workspace" subtitle="Daily work log & auto end-of-day summary">
        <Button data-testid="add-worklog-btn" disabled={!effectiveEmp} onClick={() => setOpen(true)} className="bg-navy hover:bg-navy-900 text-white rounded-lg"><Plus className="mr-2 h-4 w-4" /> Add Work Log</Button>
      </PageHeader>

      {isAdmin && (
        <div className="mb-6 max-w-sm">
          <Label>Viewing workspace of</Label>
          <Select value={selectedEmp} onValueChange={setSelectedEmp}>
            <SelectTrigger data-testid="workspace-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      {/* Workspace header card */}
      <div className="rounded-lg bg-gold text-navy-900 p-6 mb-6 flex flex-wrap items-center gap-6">
        <img src={LOGO} alt="VNS" className="h-14 w-14 object-contain bg-white rounded-lg p-1" />
        <div className="flex-1 min-w-[200px]">
          <p className="text-navy-900 text-sm font-medium">{empInfo?.employee_id || user?.employee_id || "—"}</p>
          <p className="font-heading text-xl font-bold tracking-tight">{headerName}</p>
          <p className="text-navy/70 text-xs">Reporting Head: {empInfo?.reporting_head || "R. Aadhya"} · {fmtDate(today())}</p>
        </div>
        {summary && (
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck className="h-4 w-4 text-navy-900" /> Verified by Data Entry Head
          </div>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <KpiCard testId="ws-assigned" label="Assigned" value={summary.total_assigned} icon={ClipboardList} />
          <KpiCard testId="ws-completed" label="Completed" value={summary.total_completed} icon={CheckCircle2} accent="green" />
          <KpiCard testId="ws-completion" label="Completion %" value={`${summary.completion}%`} icon={Percent} accent="gold" />
          <KpiCard testId="ws-pending" label="Pending" value={summary.pending} accent="red" />
          <KpiCard testId="ws-accuracy" label="Avg Accuracy" value={`${summary.avg_accuracy}%`} icon={ShieldCheck} accent="green" />
          <KpiCard testId="ws-hours" label="Hours / OT" value={`${summary.working_hours} / ${summary.overtime}`} icon={Timer} />
        </div>
      )}

      <SectionCard title="Work Log" testId="worklog-table-card">
        {isLoading ? <Skeleton className="h-48" /> : logs.length === 0 ? (
          <p className="text-slate-400 text-center py-8">No work logs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {["Time", "Client", "Project", "Task", "Target", "Completed", "Accuracy %", "Status", "Hours", ""].map((h) => <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l) => (
                  <TableRow key={l.log_id} data-testid={`worklog-row-${l.log_id}`} className="hover:bg-slate-50">
                    <TableCell>{l.time}</TableCell>
                    <TableCell>{l.client_name}</TableCell>
                    <TableCell>{l.project}</TableCell>
                    <TableCell className="max-w-[160px] truncate text-slate-500">{l.task_assigned}</TableCell>
                    <TableCell>{l.target}</TableCell>
                    <TableCell>{l.completed}</TableCell>
                    <TableCell>{l.accuracy}%</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_STYLES[l.status]}>{l.status}</Badge></TableCell>
                    <TableCell>{l.hours_worked}</TableCell>
                    <TableCell><Button variant="ghost" size="icon" onClick={() => del.mutate(l.log_id)}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-navy">Add Work Log Entry</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
            <div><Label>Client Name</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
            <div><Label>Project</Label><Input value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} /></div>
            <div><Label>Task Assigned</Label><Input value={form.task_assigned} onChange={(e) => setForm({ ...form, task_assigned: e.target.value })} /></div>
            <div><Label>Target</Label><Input type="number" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} /></div>
            <div><Label>Completed</Label><Input type="number" value={form.completed} onChange={(e) => setForm({ ...form, completed: e.target.value })} /></div>
            <div><Label>Accuracy %</Label><Input type="number" value={form.accuracy} onChange={(e) => setForm({ ...form, accuracy: e.target.value })} /></div>
            <div><Label>Hours Worked</Label><Input type="number" step="0.5" value={form.hours_worked} onChange={(e) => setForm({ ...form, hours_worked: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="worklog-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Completed">Completed</SelectItem><SelectItem value="In Progress">In Progress</SelectItem><SelectItem value="Pending">Pending</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Remarks</Label><Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={() => create.mutate()} data-testid="save-worklog-btn" className="bg-navy text-white">Save Entry</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
